import React, { useEffect, useMemo, useState } from "react";
import { DateInput } from "../../components/ui/DateInput";
import { useFeatureFlags } from "../../hooks/useFeatureFlags";
import { LockedFeatureEmptyState } from "../../components/billing/LockedFeatureEmptyState";
import { NotEnoughDataEmptyState } from "../../components/dashboard/NotEnoughDataEmptyState";
import { NoDataEmptyState } from "../../components/dashboard/NoDataEmptyState";
import PlansModal from "../../components/billing/PlansModal";
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid,
  BarChart, Bar, Legend,
} from "recharts";

import Sidebar from "../../components/Sidebar";
import { AppHeader } from "../../components/layout/AppHeader";
import { logger } from "../../utils/logger";

import {
  ReportsAPI, PatientTrendBucket, AppointmentUtilisation, RevenueBucket, DiagnosisRow, ReportType,
  PnlBucket, CashFlowBucket, BreakdownRow, PnlPeriod, ConsolidatedPnlRow,
} from "../../services/reports.service";
import { DepartmentOverviewRow } from "../../services/dashboard.service";
import ClinicGroupAPI, { ClinicGroup } from "../../services/clinicGroup.service";
import { ClinicGroupPanel } from "../../components/accounting/ClinicGroupPanel";
import SectionCard from "../../components/dashboard/SectionCard";
import { formatINR } from "../../components/dashboard/formatINR";

type RangePreset = "7" | "30" | "90" | "custom";

function isoDaysAgo(days: number): string {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return d.toISOString().slice(0, 10);
}

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

type AccountingTab = "pnl" | "cash-flow" | "breakdown";
type TabKey = ReportType | AccountingTab;

const REPORT_TABS: { key: ReportType; label: string }[] = [
  { key: "patients",     label: "Patients" },
  { key: "appointments", label: "Appointments" },
  { key: "departments",  label: "Departments" },
  { key: "diagnoses",    label: "Diagnoses" },
  { key: "revenue",      label: "Revenue" },
];

const ACCOUNTING_TABS: { key: AccountingTab; label: string }[] = [
  { key: "pnl",        label: "P&L" },
  { key: "cash-flow",  label: "Cash Flow" },
  { key: "breakdown",  label: "Breakdown" },
];

const ALL_TABS: { key: TabKey; label: string }[] = [...REPORT_TABS, ...ACCOUNTING_TABS];

function isAccountingTab(key: TabKey): key is AccountingTab {
  return key === "pnl" || key === "cash-flow" || key === "breakdown";
}

const ReportsPage: React.FC = () => {
  const {
    getGateState,
    getFlag,
    loading: flagsLoading,
    isAdmin,
  } = useFeatureFlags();

  const reportGate = getGateState("advanced_reports");
  const reportFlag = getFlag("advanced_reports");
  const isGated = !flagsLoading && (reportGate === "locked" || reportGate === "expired");

  const [showPlansModal, setShowPlansModal] = useState(false);
  const [preset, setPreset] = useState<RangePreset>("30");
  const [from, setFrom] = useState<string>(isoDaysAgo(30));
  const [to, setTo] = useState<string>(todayIso());
  const [activeReport, setActiveReport] = useState<TabKey>("patients");
  const [pnlPeriod, setPnlPeriod] = useState<PnlPeriod>("month");

  const [patients, setPatients] = useState<PatientTrendBucket[] | null>(null);
  const [appts, setAppts] = useState<AppointmentUtilisation | null>(null);
  const [departments, setDepartments] = useState<DepartmentOverviewRow[] | null>(null);
  const [revenue, setRevenue] = useState<RevenueBucket[] | null>(null);
  const [diagnoses, setDiagnoses] = useState<DiagnosisRow[] | null>(null);

  const [pnl, setPnl] = useState<PnlBucket[] | null>(null);
  const [cashFlow, setCashFlow] = useState<CashFlowBucket[] | null>(null);
  const [breakdown, setBreakdown] = useState<BreakdownRow[] | null>(null);

  // Cross-branch consolidation (Sub-task 6) — only offered when the caller
  // owns a clinic group, and only for the P&L tab (the only report the
  // backend's consolidated endpoint currently supports).
  const [myGroup, setMyGroup] = useState<ClinicGroup | null>(null);
  const [consolidated, setConsolidated] = useState(false);
  const [selectedBranch, setSelectedBranch] = useState<string>("all");
  const [consolidatedRows, setConsolidatedRows] = useState<ConsolidatedPnlRow[] | null>(null);

  const [loading, setLoading] = useState(false);
  const [accountingLoading, setAccountingLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);

  // Minimum data points (within the selected range) before a chart renders —
  // a near-empty chart reads as broken, not as "not much has happened yet".
  // "Prescriptions" has no dedicated report/tab on this page, so isn't gated
  // here — only the three metrics this page actually has data for.
  const MIN_DATA_POINTS = 5;
  const patientsCount  = (patients  ?? []).reduce((sum, b) => sum + b.new_patients + b.returning_patients, 0);
  const diagnosesCount = (diagnoses ?? []).reduce((sum, r) => sum + r.count, 0);
  const revenueCount   = (revenue   ?? []).reduce((sum, b) => sum + b.invoice_count, 0);

  const activeIsAccounting = isAccountingTab(activeReport);

  useEffect(() => {
    ClinicGroupAPI.getMine().then(setMyGroup).catch(() => setMyGroup(null));
  }, []);

  // Update from/to when preset changes (except in custom mode).
  useEffect(() => {
    if (preset === "custom") return;
    setFrom(isoDaysAgo(Number(preset)));
    setTo(todayIso());
  }, [preset]);

  // Load legacy report data when range changes — skip entirely when the
  // advanced_reports feature is gated (Accounting is loaded independently
  // below and is never gated).
  useEffect(() => {
    if (flagsLoading || isGated) return;

    let cancelled = false;
    setLoading(true);
    setError(null);

    const tasks = [
      ReportsAPI.patients(from, to).then((d) => { if (!cancelled) setPatients(d); }),
      ReportsAPI.appointments(from, to).then((d) => { if (!cancelled) setAppts(d); }),
      ReportsAPI.departments(from, to).then((d) => { if (!cancelled) setDepartments(d); }),
      ReportsAPI.revenue(from, to).then((d) => { if (!cancelled) setRevenue(d); }),
      ReportsAPI.diagnoses(from, to).then((d) => { if (!cancelled) setDiagnoses(d); }),
    ];

    Promise.all(tasks)
      .catch((e: Error) => {
        if (!cancelled) {
          logger.error("reports load failed", e);
          setError(e.message || "Failed to load reports");
        }
      })
      .finally(() => { if (!cancelled) setLoading(false); });

    return () => { cancelled = true; };
  }, [from, to, flagsLoading, isGated]);

  // Load Accounting data — Admin-only, NOT feature-flag gated (confirmed by
  // product, 2026-07-23): always loads regardless of the advanced_reports
  // plan gate above.
  useEffect(() => {
    let cancelled = false;
    setAccountingLoading(true);
    setError(null);

    const tasks = [
      ReportsAPI.pnl(from, to, pnlPeriod).then((d) => { if (!cancelled) setPnl(d); }),
      ReportsAPI.cashFlow(from, to).then((d) => { if (!cancelled) setCashFlow(d); }),
      ReportsAPI.breakdown(from, to).then((d) => { if (!cancelled) setBreakdown(d); }),
    ];
    if (myGroup) {
      tasks.push(
        ReportsAPI.consolidatedPnl(myGroup.clinic_group_id, from, to, pnlPeriod)
          .then((d) => { if (!cancelled) setConsolidatedRows(d); })
      );
    }

    Promise.all(tasks)
      .catch((e: Error) => {
        if (!cancelled) {
          logger.error("accounting reports load failed", e);
          setError(e.message || "Failed to load accounting reports");
        }
      })
      .finally(() => { if (!cancelled) setAccountingLoading(false); });

    return () => { cancelled = true; };
  }, [from, to, pnlPeriod, myGroup]);

  // Aggregate ConsolidatedPnlRow[] (one row per clinic per bucket) into the
  // same PnlBucket[] shape PnlReport already knows how to render — either
  // summed across every branch ("All branches") or filtered to one.
  const consolidatedPnlBuckets = useMemo((): PnlBucket[] => {
    if (!consolidatedRows) return [];
    const filtered = selectedBranch === "all"
      ? consolidatedRows
      : consolidatedRows.filter((r) => r.clinic_id === selectedBranch);

    const byBucket = new Map<string, { income: number; expense: number }>();
    for (const row of filtered) {
      const entry = byBucket.get(row.bucket) ?? { income: 0, expense: 0 };
      entry.income += row.income_paise;
      entry.expense += row.expense_paise;
      byBucket.set(row.bucket, entry);
    }
    return Array.from(byBucket.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([bucket, v]) => ({
        bucket, income_paise: v.income, expense_paise: v.expense,
        net_paise: v.income - v.expense, is_expense_anomaly: false,
      }));
  }, [consolidatedRows, selectedBranch]);

  const handleExport = async (format: "csv" | "pdf") => {
    setExporting(true);
    try {
      if (activeReport === "pnl") {
        await (format === "csv" ? ReportsAPI.downloadPnlCsv(from, to, pnlPeriod) : ReportsAPI.downloadPnlPdf(from, to, pnlPeriod));
      } else if (activeReport === "cash-flow") {
        await (format === "csv" ? ReportsAPI.downloadCashFlowCsv(from, to) : ReportsAPI.downloadCashFlowPdf(from, to));
      } else if (activeReport === "breakdown") {
        await (format === "csv" ? ReportsAPI.downloadBreakdownCsv(from, to) : ReportsAPI.downloadBreakdownPdf(from, to));
      } else {
        await ReportsAPI.downloadCsv(activeReport, from, to);
      }
    } catch (e) {
      const msg = e instanceof Error ? e.message : "Export failed";
      logger.error("reports export failed", e instanceof Error ? e : undefined);
      setError(msg);
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#f5f5f7] flex">
      <Sidebar />

      <div className="flex-1 min-w-0 flex flex-col pl-0 lg:pl-[var(--sidebar-w)] transition-[padding-left] duration-300 ease-in-out pt-14 lg:pt-0">
        <AppHeader title="Reports & Analytics" />

        <div className="flex-1 p-4 sm:p-6 lg:p-8 space-y-5">
          {flagsLoading && (
            <div className="h-72 rounded-2xl bg-white border border-slate-100 shadow-sm animate-pulse" />
          )}

          {/* Filters bar — one shared range picker for every tab. Export
              label/behaviour and the P&L period dropdown adapt to whichever
              tab is active. */}
          {!flagsLoading && (
          <>
          <div className="rounded-2xl bg-white border border-slate-100 shadow-sm px-5 py-4 flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-xs font-medium text-slate-500 mr-1">Range:</span>
              {([
                { v: "7" as const, label: "Last 7 days" },
                { v: "30" as const, label: "Last 30 days" },
                { v: "90" as const, label: "Last 90 days" },
                { v: "custom" as const, label: "Custom" },
              ]).map((p) => (
                <button
                  key={p.v}
                  onClick={() => setPreset(p.v)}
                  className={`text-xs font-semibold px-3 py-1.5 rounded-lg border transition-colors ${
                    preset === p.v
                      ? "bg-blue-600 text-white border-blue-600"
                      : "bg-white text-slate-600 border-slate-200 hover:border-slate-300"
                  }`}
                >
                  {p.label}
                </button>
              ))}
              {preset === "custom" && (
                <div className="flex items-center gap-2 ml-2">
                  <DateInput
                    value={from}
                    max={to}
                    onChange={(e) => setFrom(e.target.value)}
                    className="text-xs px-2 py-1.5 border border-slate-200 rounded-lg"
                  />
                  <span className="text-xs text-slate-400">to</span>
                  <DateInput
                    value={to}
                    min={from}
                    max={todayIso()}
                    onChange={(e) => setTo(e.target.value)}
                    className="text-xs px-2 py-1.5 border border-slate-200 rounded-lg"
                  />
                </div>
              )}
              {activeReport === "pnl" && (
                <select
                  value={pnlPeriod}
                  onChange={(e) => setPnlPeriod(e.target.value as PnlPeriod)}
                  className="text-xs px-2 py-1.5 border border-slate-200 rounded-lg ml-2"
                >
                  <option value="month">Monthly</option>
                  <option value="quarter">Quarterly</option>
                  <option value="year">Yearly</option>
                </select>
              )}
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={() => handleExport("csv")}
                disabled={exporting}
                className="text-xs font-semibold px-4 py-2 rounded-lg bg-slate-900 text-white hover:bg-slate-800 disabled:bg-slate-300 disabled:cursor-not-allowed flex items-center gap-2"
                title="Download CSV"
              >
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M7 10l5 5 5-5M12 15V3" />
                </svg>
                {exporting ? "Exporting…" : `Export ${activeReport} CSV`}
              </button>
              {activeIsAccounting && (
                <button
                  onClick={() => handleExport("pdf")}
                  disabled={exporting}
                  className="text-xs font-semibold px-4 py-2 rounded-lg bg-slate-700 text-white hover:bg-slate-600 disabled:bg-slate-300"
                  title="Download PDF"
                >
                  {exporting ? "…" : "PDF"}
                </button>
              )}
            </div>
          </div>

          {/* Unified report tabs — legacy analytics tabs plus Accounting
              (P&L / Cash Flow / Breakdown) all live in one row now. Legacy
              tabs still respect the advanced_reports plan gate; Accounting
              never does (confirmed by product, 2026-07-23). */}
          <div className="bg-white rounded-2xl border border-slate-100 shadow-sm px-2 py-2 flex items-center gap-1 overflow-x-auto">
            {ALL_TABS.map((t) => {
              const isActive = t.key === activeReport;
              return (
                <button
                  key={t.key}
                  onClick={() => setActiveReport(t.key)}
                  className={`text-sm font-medium px-4 py-2 rounded-xl transition-colors whitespace-nowrap ${
                    isActive
                      ? "bg-blue-600 text-white shadow-sm"
                      : "text-slate-600 hover:bg-slate-50"
                  }`}
                >
                  {t.label}
                </button>
              );
            })}
          </div>

          {error && (
            <div className="rounded-2xl bg-red-50 border border-red-100 px-5 py-3 text-sm text-red-700">
              {error}
            </div>
          )}

          {!activeIsAccounting && isGated && (
            <div className="rounded-2xl bg-white border border-slate-100 shadow-sm">
              <LockedFeatureEmptyState
                featureLabel="Advanced Reports & Analytics"
                description="Upgrade your plan to access patient trends, revenue, appointment utilisation and diagnosis analytics."
                isAdmin={isAdmin}
                variant={reportGate === "expired" ? "expired" : "locked"}
                expiredDate={reportFlag?.expires_at ?? null}
                onUpgradeClick={() => setShowPlansModal(true)}
                onNotifyAdmin={() => {
                  window.location.href =
                    "mailto:?subject=Upgrade%20Kaagazy%20EMR%20Subscription&body=Hi%2C%20please%20upgrade%20our%20subscription%20to%20access%20Reports%20%26%20Analytics.";
                }}
              />
            </div>
          )}

          {!activeIsAccounting && !isGated && loading && (
            <div className="h-72 rounded-2xl bg-white border border-slate-100 shadow-sm animate-pulse" />
          )}

          {!activeIsAccounting && !isGated && !loading && activeReport === "patients" && (
            patientsCount < MIN_DATA_POINTS
              ? <SectionCard title="Patient Registration Trend">
                  <NotEnoughDataEmptyState metricLabel="patients" threshold={MIN_DATA_POINTS} current={patientsCount} />
                </SectionCard>
              : <PatientsReport data={patients ?? []} />
          )}

          {!activeIsAccounting && !isGated && !loading && activeReport === "appointments" && (
            <AppointmentsReport data={appts} />
          )}

          {!activeIsAccounting && !isGated && !loading && activeReport === "departments" && (
            <DepartmentsReport rows={departments ?? []} />
          )}

          {!activeIsAccounting && !isGated && !loading && activeReport === "diagnoses" && (
            diagnosesCount < MIN_DATA_POINTS
              ? <SectionCard title="Top Diagnoses">
                  <NotEnoughDataEmptyState metricLabel="diagnosis visits" threshold={MIN_DATA_POINTS} current={diagnosesCount} />
                </SectionCard>
              : <DiagnosesReport rows={diagnoses ?? []} />
          )}

          {!activeIsAccounting && !isGated && !loading && activeReport === "revenue" && (
            revenueCount < MIN_DATA_POINTS
              ? <SectionCard title="Revenue Trend">
                  <NotEnoughDataEmptyState metricLabel="bills" threshold={MIN_DATA_POINTS} current={revenueCount} />
                </SectionCard>
              : <RevenueReport data={revenue ?? []} />
          )}

          {activeIsAccounting && (
            <>
              <ClinicGroupPanel group={myGroup} onChange={setMyGroup} />

              {myGroup && activeReport === "pnl" && (
                <div className="rounded-xl bg-white border border-slate-100 shadow-sm px-4 py-3 flex flex-wrap items-center gap-3">
                  <label className="flex items-center gap-2 text-sm font-medium text-slate-700 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={consolidated}
                      onChange={(e) => setConsolidated(e.target.checked)}
                      className="rounded border-slate-300"
                    />
                    Consolidated view ({myGroup.name})
                  </label>
                  {consolidated && (
                    <select
                      value={selectedBranch}
                      onChange={(e) => setSelectedBranch(e.target.value)}
                      className="text-xs px-2 py-1.5 border border-slate-200 rounded-lg"
                    >
                      <option value="all">All branches</option>
                      {myGroup.member_clinics.map((c) => (
                        <option key={c.clinic_id} value={c.clinic_id}>{c.name}</option>
                      ))}
                    </select>
                  )}
                </div>
              )}

              {accountingLoading && (
                <div className="h-72 rounded-2xl bg-white border border-slate-100 shadow-sm animate-pulse" />
              )}

              {!accountingLoading && activeReport === "pnl" && (
                <PnlReport data={consolidated ? consolidatedPnlBuckets : (pnl ?? [])} />
              )}
              {!accountingLoading && activeReport === "cash-flow" && <CashFlowReport data={cashFlow ?? []} />}
              {!accountingLoading && activeReport === "breakdown" && <BreakdownReport rows={breakdown ?? []} />}

              <p className="text-[11px] text-slate-400 px-1">
                Anomaly highlights are a simple trailing-average heuristic, not fraud detection — always double-check flagged buckets before acting on them.
              </p>
            </>
          )}
          </>
          )}
        </div>
      </div>
      {showPlansModal && <PlansModal onClose={() => setShowPlansModal(false)} />}
    </div>
  );
};

export const PnlReport: React.FC<{ data: PnlBucket[] }> = ({ data }) => {
  const chartData = useMemo(
    () => data.map((d) => ({ ...d, bucket: d.bucket.slice(0, 7) })),
    [data],
  );
  const totalIncome = data.reduce((a, d) => a + d.income_paise, 0);
  const totalExpense = data.reduce((a, d) => a + d.expense_paise, 0);

  if (data.length === 0) {
    return (
      <SectionCard title="P&L">
        <NoDataEmptyState metricLabel="income or expenses" />
      </SectionCard>
    );
  }

  return (
    <SectionCard title={`P&L — Net ${formatINR(totalIncome - totalExpense)} (Income ${formatINR(totalIncome)} · Expenses ${formatINR(totalExpense)})`}>
      <div className="h-72">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={chartData} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
            <defs>
              <linearGradient id="pnlIncomeGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#10b981" stopOpacity={0.4} />
                <stop offset="100%" stopColor="#10b981" stopOpacity={0} />
              </linearGradient>
              <linearGradient id="pnlExpenseGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#ef4444" stopOpacity={0.3} />
                <stop offset="100%" stopColor="#ef4444" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
            <XAxis dataKey="bucket" tick={{ fontSize: 11, fill: "#64748b" }} axisLine={false} tickLine={false} />
            <YAxis tick={{ fontSize: 11, fill: "#64748b" }} axisLine={false} tickLine={false}
                   tickFormatter={(v) => formatINR(v)} />
            <Tooltip
              contentStyle={{ fontSize: 12, borderRadius: 8, border: "1px solid #e2e8f0" }}
              formatter={(v) => formatINR(Number(v))}
            />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            <Area type="monotone" dataKey="income_paise"  name="Income"   stroke="#10b981" fill="url(#pnlIncomeGrad)" strokeWidth={2} />
            <Area type="monotone" dataKey="expense_paise" name="Expenses" stroke="#ef4444" fill="url(#pnlExpenseGrad)" strokeWidth={2} />
          </AreaChart>
        </ResponsiveContainer>
      </div>
      {data.some((d) => d.is_expense_anomaly) && (
        <p className="text-xs text-amber-600 mt-2">
          ⚠ {data.filter((d) => d.is_expense_anomaly).length} bucket(s) flagged for unusual expense levels vs. the trailing average.
        </p>
      )}
    </SectionCard>
  );
};

export const CashFlowReport: React.FC<{ data: CashFlowBucket[] }> = ({ data }) => {
  const chartData = useMemo(
    () => data.map((d) => ({ ...d, bucket: d.bucket.slice(5) })),
    [data],
  );
  const totalIn = data.reduce((a, d) => a + d.money_in_paise, 0);
  const totalOut = data.reduce((a, d) => a + d.money_out_paise, 0);

  if (data.length === 0) {
    return (
      <SectionCard title="Cash Flow">
        <NoDataEmptyState metricLabel="cash movement" />
      </SectionCard>
    );
  }

  return (
    <SectionCard title={`Cash Flow — Net ${formatINR(totalIn - totalOut)} (In ${formatINR(totalIn)} · Out ${formatINR(totalOut)})`}>
      <div className="h-72">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={chartData} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
            <XAxis dataKey="bucket" tick={{ fontSize: 11, fill: "#64748b" }} axisLine={false} tickLine={false} />
            <YAxis tick={{ fontSize: 11, fill: "#64748b" }} axisLine={false} tickLine={false}
                   tickFormatter={(v) => formatINR(v)} />
            <Tooltip
              contentStyle={{ fontSize: 12, borderRadius: 8, border: "1px solid #e2e8f0" }}
              formatter={(v) => formatINR(Number(v))}
            />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            <Bar dataKey="money_in_paise"  name="Money In"  fill="#3b82f6" radius={[6, 6, 0, 0]} />
            <Bar dataKey="money_out_paise" name="Money Out" fill="#f97316" radius={[6, 6, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
      {data.some((d) => d.is_outflow_anomaly) && (
        <p className="text-xs text-amber-600 mt-2">
          ⚠ {data.filter((d) => d.is_outflow_anomaly).length} bucket(s) flagged for unusual outflow vs. the trailing average.
        </p>
      )}
    </SectionCard>
  );
};

export const BreakdownReport: React.FC<{ rows: BreakdownRow[] }> = ({ rows }) => {
  if (rows.length === 0) {
    return (
      <SectionCard title="Category / Source Breakdown">
        <NoDataEmptyState metricLabel="categorized transactions" />
      </SectionCard>
    );
  }
  const kindLabel: Record<BreakdownRow["kind"], string> = {
    expense_category: "Expense",
    income_source: "Other Income",
    invoice_revenue: "Consultation Revenue",
  };
  return (
    <SectionCard title="Category / Source Breakdown">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-slate-200 text-left text-xs uppercase tracking-wide text-slate-500">
              <th className="py-2 pr-4">Type</th>
              <th className="py-2 pr-4">Label</th>
              <th className="py-2 pr-4 text-right">Amount</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i} className="border-b border-slate-100 last:border-0">
                <td className="py-2 pr-4 text-slate-500">{kindLabel[r.kind]}</td>
                <td className="py-2 pr-4 text-slate-700">{r.label}</td>
                <td className="py-2 pr-4 text-right font-medium text-slate-700">{formatINR(r.amount_paise)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </SectionCard>
  );
};

// ─── Patients ─────────────────────────────────────────────────────────────────

export const PatientsReport: React.FC<{ data: PatientTrendBucket[] }> = ({ data }) => {
  const chartData = useMemo(
    () => data.map((d) => ({ ...d, bucket: d.bucket.slice(5) })),
    [data],
  );

  return (
    <SectionCard title="Patient Registration Trend">
      <div className="h-72">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={chartData} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
            <defs>
              <linearGradient id="newGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#3b82f6" stopOpacity={0.4} />
                <stop offset="100%" stopColor="#3b82f6" stopOpacity={0} />
              </linearGradient>
              <linearGradient id="retGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#10b981" stopOpacity={0.4} />
                <stop offset="100%" stopColor="#10b981" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
            <XAxis dataKey="bucket" tick={{ fontSize: 11, fill: "#64748b" }} axisLine={false} tickLine={false} />
            <YAxis tick={{ fontSize: 11, fill: "#64748b" }} axisLine={false} tickLine={false} />
            <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8, border: "1px solid #e2e8f0" }} />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            <Area type="monotone" dataKey="new_patients"       name="New"       stroke="#3b82f6" fill="url(#newGrad)" strokeWidth={2} />
            <Area type="monotone" dataKey="returning_patients" name="Returning" stroke="#10b981" fill="url(#retGrad)" strokeWidth={2} />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </SectionCard>
  );
};

// ─── Appointments ─────────────────────────────────────────────────────────────

const AppointmentsReport: React.FC<{ data: AppointmentUtilisation | null }> = ({ data }) => {
  if (!data) return <p className="text-sm text-slate-400">No data.</p>;

  const cards = [
    { label: "Booked Slots",     value: data.booked_slots.toLocaleString("en-IN") },
    { label: "Available Slots",  value: data.available_slots.toLocaleString("en-IN") },
    { label: "Utilisation",      value: `${data.utilisation_pct.toFixed(1)}%` },
    { label: "Cancellation",     value: `${data.cancellation_rate_pct.toFixed(1)}%` },
    { label: "No-show",          value: `${data.no_show_rate_pct.toFixed(1)}%` },
  ];

  return (
    <SectionCard title="Appointment Utilisation">
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
        {cards.map((c) => (
          <div key={c.label} className="rounded-xl border border-slate-100 bg-slate-50 px-4 py-3">
            <p className="text-[11px] font-medium text-slate-500">{c.label}</p>
            <p className="text-lg font-bold text-slate-900 mt-1">{c.value}</p>
          </div>
        ))}
      </div>
    </SectionCard>
  );
};

// ─── Departments ──────────────────────────────────────────────────────────────

const DepartmentsReport: React.FC<{ rows: DepartmentOverviewRow[] }> = ({ rows }) => {
  const data = useMemo(
    () =>
      rows.map((r) => ({
        department: r.department || "Unspecified",
        Patients: r.patient_count,
        Appointments: r.appointment_count,
      })),
    [rows],
  );

  return (
    <SectionCard title="Department Overview">
      <div className="h-72">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
            <XAxis dataKey="department" tick={{ fontSize: 11, fill: "#64748b" }} axisLine={false} tickLine={false} />
            <YAxis tick={{ fontSize: 11, fill: "#64748b" }} axisLine={false} tickLine={false} />
            <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8, border: "1px solid #e2e8f0" }} />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            <Bar dataKey="Patients"     fill="#3b82f6" radius={[6, 6, 0, 0]} />
            <Bar dataKey="Appointments" fill="#a855f7" radius={[6, 6, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </SectionCard>
  );
};

// ─── Revenue ──────────────────────────────────────────────────────────────────

export const RevenueReport: React.FC<{ data: RevenueBucket[] }> = ({ data }) => {
  const chartData = useMemo(
    () => data.map((d) => ({
      day: d.bucket.slice(5),
      Revenue: d.amount_paise / 100,         // rupees for axis readability
      invoices: d.invoice_count,
    })),
    [data],
  );
  const totalPaise = data.reduce((acc, d) => acc + d.amount_paise, 0);
  const totalInvoices = data.reduce((acc, d) => acc + d.invoice_count, 0);

  return (
    <SectionCard title={`Revenue — ${formatINR(totalPaise)} across ${totalInvoices} invoice${totalInvoices === 1 ? "" : "s"}`}>
      <div className="h-72">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={chartData} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
            <defs>
              <linearGradient id="revGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#10b981" stopOpacity={0.5} />
                <stop offset="100%" stopColor="#10b981" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
            <XAxis dataKey="day" tick={{ fontSize: 11, fill: "#64748b" }} axisLine={false} tickLine={false} />
            <YAxis tick={{ fontSize: 11, fill: "#64748b" }} axisLine={false} tickLine={false}
                   tickFormatter={(v) => v >= 1000 ? `${(v / 1000).toFixed(0)}K` : String(v)} />
            <Tooltip
              contentStyle={{ fontSize: 12, borderRadius: 8, border: "1px solid #e2e8f0" }}
              formatter={(v, name) => name === "Revenue" ? `₹${Number(v).toLocaleString("en-IN")}` : v}
            />
            <Area type="monotone" dataKey="Revenue" stroke="#10b981" strokeWidth={2} fill="url(#revGrad)" />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </SectionCard>
  );
};

// ─── Diagnoses ────────────────────────────────────────────────────────────────

export const DiagnosesReport: React.FC<{ rows: DiagnosisRow[] }> = ({ rows }) => {
  if (rows.length === 0) {
    return (
      <SectionCard title="Top 10 Diagnoses">
        <p className="text-sm text-slate-400">No diagnoses recorded for this period.</p>
      </SectionCard>
    );
  }

  return (
    <SectionCard title="Top 10 Diagnoses">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-slate-200 text-left text-xs uppercase tracking-wide text-slate-500">
              <th className="py-2 pr-4 w-12">Rank</th>
              <th className="py-2 pr-4">Diagnosis</th>
              {/* ICD-10 column hidden until S5-02 backfill lands. Backend
                  still returns icd10_code in the JSON / CSV — uncomment the
                  <th> + <td> below to surface it. */}
              {/* <th className="py-2 pr-4 w-24">ICD-10</th> */}
              <th className="py-2 pr-4 w-20 text-right">Count</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.rank} className="border-b border-slate-100 last:border-0">
                <td className="py-2 pr-4 text-slate-500">{r.rank}</td>
                <td className="py-2 pr-4 text-slate-700">{r.diagnosis}</td>
                {/* <td className="py-2 pr-4 text-slate-500">{r.icd10_code ?? "—"}</td> */}
                <td className="py-2 pr-4 text-right font-medium text-slate-700">{r.count}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </SectionCard>
  );
};

export default ReportsPage;
